/**
 * <license header>
 */

import { MenuItem, AdminUiSdkRegistration } from './types';

/**
 * AdminUiSdk class for managing Admin UI extensions
 * Provides methods to create menu items, sections, and pages for Adobe Commerce Admin UI extensions
 */
export class AdminUiSdk {
  private extensionId: string;
  private menuItems: MenuItem[] = [];
  private pageTitle?: string;

  /**
   * Creates a new AdminUiSdk instance
   * @param extensionId - Unique identifier for the extension
   * @throws {Error} If extensionId is empty or invalid
   */
  constructor(extensionId: string) {
    if (!extensionId?.trim()) {
      throw new Error('Extension ID is required and cannot be empty');
    }

    const trimmedId = extensionId.trim();
    if (!this.isValidExtensionId(trimmedId)) {
      throw new Error(
        'Extension ID must be alphanumeric with underscores only (no spaces, hyphens, or special characters)'
      );
    }

    this.extensionId = trimmedId;
  }

  /**
   * Validates that an extension ID contains only alphanumeric characters and underscores
   * @param id - The extension ID to validate
   * @returns true if valid, false otherwise
   */
  private isValidExtensionId(id: string): boolean {
    return /^[a-zA-Z0-9_]+$/.test(id);
  }

  /**
   * Validates that a menu ID is valid (can contain :: separator for namespacing)
   * @param id - The menu ID to validate
   * @returns true if valid, false otherwise
   */
  private isValidMenuId(id: string): boolean {
    return /^[a-zA-Z0-9_:]+$/.test(id);
  }

  /**
   * Adds a menu item to the extension
   * @param id - Full identifier for the menu item (e.g., 'extensionId::menuItem')
   * @param title - Display title for the menu item
   * @param sortOrder - Sort order for menu positioning
   * @param parent - Parent menu identifier (optional, full ID like 'extensionId::parent')
   * @throws {Error} If parameters are invalid or ID already exists
   */
  addMenuItem(id: string, title: string, sortOrder: number, parent?: string): void {
    // Validation
    if (!id?.trim()) {
      throw new Error('Menu item ID is required and cannot be empty');
    }
    if (!this.isValidMenuId(id.trim())) {
      throw new Error(
        'Menu item ID must be alphanumeric with underscores and colons only (no spaces, hyphens, or other special characters)'
      );
    }
    if (!title?.trim()) {
      throw new Error('Menu item title is required and cannot be empty');
    }
    if (parent !== undefined && !parent?.trim()) {
      throw new Error('Menu item parent cannot be empty if provided');
    }
    if (parent !== undefined && !this.isValidMenuId(parent.trim())) {
      throw new Error(
        'Menu item parent must be alphanumeric with underscores and colons only (no spaces, hyphens, or other special characters)'
      );
    }
    if (typeof sortOrder !== 'number' || sortOrder < 0) {
      throw new Error('Menu item sortOrder must be a non-negative number');
    }

    const trimmedId = id.trim();

    // Check for duplicate IDs
    if (this.menuItems.some(item => item.id === trimmedId)) {
      throw new Error(`Menu item with ID '${trimmedId}' already exists`);
    }

    // Build menu item object - only include parent if provided
    const menuItem: MenuItem = {
      id: trimmedId,
      title: title.trim(),
      sortOrder,
    };

    if (parent?.trim()) {
      menuItem.parent = parent.trim();
    }

    // Add sanitized menu item
    this.menuItems.push(menuItem);
  }

  /**
   * Adds a menu section to the extension
   * @param id - Full identifier for the menu section (e.g., 'extensionId::section')
   * @param title - Display title for the menu section
   * @param sortOrder - Sort order for section positioning
   * @param parent - Parent menu identifier (optional, full ID like 'Magento_Backend::system')
   * @throws {Error} If parameters are invalid or ID already exists
   */
  addMenuSection(id: string, title: string, sortOrder: number, parent?: string): void {
    // Validation
    if (!id?.trim()) {
      throw new Error('Menu section ID is required and cannot be empty');
    }
    if (!this.isValidMenuId(id.trim())) {
      throw new Error(
        'Menu section ID must be alphanumeric with underscores and colons only (no spaces, hyphens, or other special characters)'
      );
    }
    if (!title?.trim()) {
      throw new Error('Menu section title is required and cannot be empty');
    }
    if (parent !== undefined && !parent?.trim()) {
      throw new Error('Menu section parent cannot be empty if provided');
    }
    if (parent !== undefined && !this.isValidMenuId(parent.trim())) {
      throw new Error(
        'Menu section parent must be alphanumeric with underscores and colons only (no spaces, hyphens, or other special characters)'
      );
    }
    if (typeof sortOrder !== 'number' || sortOrder < 0) {
      throw new Error('Menu section sortOrder must be a non-negative number');
    }

    const trimmedId = id.trim();

    // Check for duplicate IDs
    if (this.menuItems.some(item => item.id === trimmedId)) {
      throw new Error(`Menu item with ID '${trimmedId}' already exists`);
    }

    // Build menu section object - only include parent if provided
    const menuSection: MenuItem = {
      id: trimmedId,
      title: title.trim(),
      sortOrder,
      isSection: true,
    };

    if (parent?.trim()) {
      menuSection.parent = parent.trim();
    }

    // Add sanitized menu section
    this.menuItems.push(menuSection);
  }

  /**
   * Sets the page title for the extension
   * @param title - The page title
   * @throws {Error} If title is empty or invalid
   */
  addPage(title: string): void {
    if (!title?.trim()) {
      throw new Error('Page title is required and cannot be empty');
    }
    this.pageTitle = title.trim();
  }

  /**
   * Gets the complete registration object for the extension
   * @returns The registration object with optional menu items and page configuration
   */
  getRegistration(): AdminUiSdkRegistration {
    const registration: any = {};

    // Only include menuItems if there are any
    if (this.menuItems.length > 0) {
      registration.menuItems = [...this.menuItems]; // Return copy to prevent mutation
    }

    // Only include page if title is set
    if (this.pageTitle) {
      registration.page = {
        title: this.pageTitle,
      };
    }

    return {
      registration,
    };
  }
}
